import React, { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import DateDialog from 'components/dialogs/DateDialog';
import NotifyDialog from 'components/dialogs/NotifyDialog';
import PromptDialog from 'components/dialogs/PromptDialog';
import UploadFileDialog from 'components/dialogs/UploadFileDialog';
import useModal from 'hooks/useModal';
import { CarModel, CarYard, StockStatus } from 'models/StockModel';
import { CompanyType } from 'models/CompanyModel';
import { TransportationModel } from 'models/TransportationModel';
import useCars from 'modules/cars/hooks/useCars';
import CarAddDialog from 'modules/cars/dialogs/CarAddDialog';
import ConfirmModelDialog from 'modules/cars/dialogs/ConfirmModelDialog';
import CarEditDialog from 'modules/cars/dialogs/CarEditDialog';
import YardDialog from 'modules/cars/dialogs/YardDialog';
import UploadCarsSpreadsheetDialog from 'modules/cars/dialogs/UploadCarsSpreadsheetDialog';
import UploadSpreadsheetDialog from 'modules/cars/dialogs/UploadSpreadsheetDialog';
import UploadBatchPdfMultiPageDialog from 'modules/cars/dialogs/UploadBatchPdfMultiPageDialog';
import UploadBatchPdfDialog from 'modules/cars/dialogs/UploadBatchPdfDialog';
import ChangeChassisDialog from 'modules/cars/dialogs/ChangeChassisDialog';
import ShipDateLimitDialog from 'modules/cars/dialogs/ShipDateLimitDialog';
import InnerCargoDialog from 'modules/cars/dialogs/InnerCargoDialog';
import AlbumDialog from 'modules/cars/dialogs/AlbumDialog';
import NotesDialog from 'modules/cars/dialogs/NotesDialog';
import ShipperDialog from 'modules/cars/dialogs/ShipperDialog';
import YardInDialog from 'modules/cars/dialogs/YardInDialog';
import FilterDialog from 'modules/cars/dialogs/FilterDialog';
import ActivityDialog from 'modules/cars/dialogs/ActivityDialog';
import YardActivityDialog from 'modules/cars/dialogs/YardActivityDialog';
import SelectVoyageDialog from 'modules/voyages/dialogs/SelectVoyageDialog';
import PhotoAdder from 'modules/cars/common/PhotoAdder';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';


const useCarModals = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const {showModal, hideModal, hideAll} = useModal();
    const {
        updateCar,
        cancelCar,
        moveToYard,
        batchCreate,
        batchUploadEC,
        batchUploadECMultiPage,
        batchUploadPhotos,
        putInnerCargo,
        changeChassis,
        cancelBooking,
        setVoyage,
        changeShipper,
        setShipDateLimit,
    } = useCars();
    const navigate = useNavigate();
    const {enqueueSnackbar} = useSnackbar();

    const openAddCarModal = () => showModal(CarAddDialog,
        {},
        {
            title: t('add_car'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(Api.cars.create)
        .then(res => {
            if (res.data.status === 'shipped') {
                enqueueSnackbar(t('car_already_shipped'), {
                    variant: 'error',
                });
            } else if (res.data.status === 'confirm') {
                return showModal(ConfirmModelDialog,
                    {
                        makers: res.data.makers,
                        models: res.data.models,
                    },
                    {
                        title: t('confirm'),
                        maxWidth: 'xs',
                        fullWidth: true,
                    })
                    .then(data => Api.cars.createStep2(res.data.id, data))
                    .then(() => navigate(`/car/${res.data.id}`))
            } else {
                setTimeout(() => navigate(`/car/${res.data.id}`), 1000);
            }
        })

    const openEditCarModal = (car: CarModel) => showModal(CarEditDialog,
        {car},
        {
            title: t('edit_title', {value: `${car.model} ${car.chassisNumber}`}),
            maxWidth: 'md',
            fullWidth: true,
        })
        .then(updateCar)
        .then(hideModal);

    const openViewPhotosModal = (car: CarModel, startAlbum: string) => showModal(AlbumDialog,
        {
            car,
            startAlbum,
        },
        {
            fullScreen: true,
        });

    const openAddPhotosModal = (car: CarModel) => showModal(PhotoAdder,
        {car},
        {
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(hideModal);

    const openYardModal = (cars: CarModel[], isLoading: boolean = true) => showModal(YardDialog,
        {
            from: cars[0].yard?.id,
            isLoading,
        },
        {
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(to => moveToYard(cars.map(c => c.id), to))
        .then(hideModal);

    const openVoyageModal = (cars: CarModel[]) => showModal(SelectVoyageDialog,
        {car: cars[0]},
        {
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(voyageId => {
            const port = cars[0].yard!.portId;
            return setVoyage(cars.filter(c => c.yard?.portId === port).map(c => c.id), voyageId)
        })
        .then(hideModal);

    const confirmCancelShipping = (car: CarModel) => showModal(PromptDialog,
        {
            description: `${car.make} ${car.model} ${car.chassisNumber}`,
            required: true
        },
        {
            title: t('reason_of_shipping_cancellation'),
            fullScreen: false,
        })
        .then(reason => cancelCar(car, reason))
        .then(() => showModal(ConfirmDialog,
            {},
            {
                title: t('confirm_create_cancellation_invoice'),
                fullScreen: false,
            }))
        .then(() => Api.invoices.createShippingCancellation(car.id))
        .then(data => navigate(`/invoice/${data.invoice_id}`))
        .catch(hideAll);

    const confirmCancelBooking = (cars: CarModel[]) => showModal(PromptDialog,
        {
            description: cars.map((car, i) => (
                <Fragment key={i}>
                    {car.chassisNumber} {car.make} {car.model}
                    {i < cars.length - 1 && <br />}
                </Fragment>
            )),
            required: true
        },
        {
            title: t('reason_of_booking_cancellation'),
            fullScreen: false,
        })
        .then(reason => cancelBooking(cars.map(car => car.id), reason))
        .then(hideModal)
        .catch(hideModal);

    const openYardInSingleModal = (car: CarModel) => showModal(DateDialog,
        {
            description: `${car.model} ${car.chassisNumber}`,
            required: true,
            defaultDate: DateTime.now(),
        },
        {
            title: t('yard_in'),
            maxWidth: 'xs',
        })
        .then(date => Api.cars.yardIn({dates: {[car.id]: date}, yardId: car.yard!.id}))
        .then(hideModal);

    const openBatchCarCreateModal = () => showModal(UploadCarsSpreadsheetDialog,
        {
            requiredColumns: ['chassis_number', 'make', 'model'],
            arbitraryColumns: [
                ...(my.type === CompanyType.Shipper ? [] : ['shipper']),
                'unit_price',
                'price_terms',
                'voyage',
                'export_cert_reference_num',
                'export_cert_valid_until',
                'export_type',
                'export_method',
                'vehicle_type',
                'color',
            ],
            secondaryColumns: [
                'year',
                'fuel',
                'engine_model',
                'engine_number',
                'engine_size',
                'engine_power',
                'seats',
                'cargo_capacity',
                'net_weight',
                'gross_weight',
                'length',
                'width',
                'height',
            ],
            consigneeColumns: [
                'consignee_country',
                'consignee_city',
                'consignee_address',
                'consignee_phone',
                'consignee_email',
                'consignee_last_name',
                'consignee_first_name',
                'consignee_middle_name',
                'consignee_company_name',
                'consignee_company_code',
                'consignee_tin',
            ],
            notifyColumns: [
                'notify_country',
                'notify_city',
                'notify_address',
                'notify_phone',
                'notify_email',
                'notify_last_name',
                'notify_first_name',
                'notify_middle_name',
                'notify_company_name',
                'notify_company_code',
                'notify_tin',
            ],
            dealerColumns: [
                'dealer_country',
                'dealer_city',
                'dealer_address',
                'dealer_phone',
                'dealer_email',
                'dealer_last_name',
                'dealer_first_name',
                'dealer_middle_name',
                'dealer_company_name',
                'dealer_company_code',
                'dealer_tin',
            ],
        },
        {
            title: t('batch_upload_for_cars'),
            maxWidth: 'lg',
            fullWidth: true,
        })
        .then(batchCreate)
        .then(data => {
            if (data.unknownCompanies.length > 0) {
                showModal(NotifyDialog,
                    {
                        text1: (
                            <ol>
                                {data.unknownCompanies.map((c: string) => <li key={c}>{c}</li>)}
                            </ol>
                        ),
                        text2: 'Other cars have been added successfully!',
                    },
                    {
                        title: t('add_this_shippers'),
                        maxWidth: 'xs',
                        fullWidth: true,
                    },
                    true
                );
            } else {
                hideModal();
            }
        });

    const openBatchInYardModal = (yardId: number, yardName: string) => showModal(UploadSpreadsheetDialog,
        {
            requiredColumns: ['in', 'chassis_number', 'shipper'],
            arbitraryColumns: ['remark', 'make', 'model', 'key', 'location']
        },
        {
            title: t('batch_yard_in_filler_for', {value: yardName}),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(data => Api.cars.batchYardIn({...data, yardId}))
        .then(hideModal);

    const openBatchUploadECModal = () => showModal(UploadBatchPdfDialog,
        {},
        {
            title: t('upload_export_certificate_batch', {value: t('separated')}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(batchUploadEC)
        .then(hideModal);

    const openBatchUploadECMultiPageModal = () => showModal(UploadBatchPdfMultiPageDialog,
        {},
        {
            title: t('upload_export_certificate_batch', {value: t('multi_page')}),
            maxWidth: 'xl',
            fullWidth: true,
        })
        .then(batchUploadECMultiPage)
        .then(hideModal);

    const openBatchUploadPhotosModal = () => showModal(UploadFileDialog,
        {
            title: t('upload_photos_batch_description'),
            accept: {
                'application/zip': ['.zip'],
            },
        },
        {
            title: t('upload_photos_batch'),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(files => batchUploadPhotos(files[0]))
        .then(hideModal);

    const openYardInModal = (yardId: number, yardName: string) => showModal(YardInDialog,
        {id: yardId},
        {
            title: t('yard_in_filler_for', {value: yardName}),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(dates => Api.cars.yardIn({dates, yardId}))
        .then(hideModal);

    const openInnerCargoModal = (car: CarModel) => showModal(InnerCargoDialog,
        {car},
        {
            title: t('inner_cargo_of', {value: car.chassisNumber}),
            maxWidth: 'md',
        })
        .then(cargo => putInnerCargo(car.id, cargo))
        .then(hideModal);

    const openChangeChassisModal = (car: CarModel) => showModal(ChangeChassisDialog,
        {car},
        {
            title: t('change_chassis_number'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(chassis => changeChassis(car.id, chassis))
        .then(hideModal);

    const openChangeShipperModal = (car: CarModel) => showModal(ShipperDialog,
        {car},
        {
            title: t('change_shipper'),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(companyId => changeShipper(car.id, companyId))
        .then(hideModal);

    const openShipDateLimitModal = (car: CarModel) => showModal(ShipDateLimitDialog,
        {},
        {
            title: t('ship_date_limiter_for', {value: `${car.model} ${car.chassisNumber}`}),
            maxWidth: 'xs',
            fullWidth: true,
        })
        .then(({after, date}) => setShipDateLimit(car.id, after, date))
        .then(hideModal);

    const openEditNotesModal = (car: CarModel) => showModal(NotesDialog,
        {car},
        {
            title: t('edit_title', {value: t('notes')}),
            maxWidth: 'sm',
        })
        .then(notes => Api.cars.setNotes(car.id, notes))
        .then(hideModal);

    const confirmRestoreCar = (car: CarModel) => showModal(ConfirmDialog,
        {
            description: `${car.model} ${car.chassisNumber}`,
        },
        {
            title: t('confirm_restore'),
            fullScreen: false,
        })
        .then(() => Api.cars.setStatus(car.id, StockStatus.Pending))
        .finally(hideModal);

    const confirmRemoveCar = (car: CarModel) => showModal(ConfirmDialog,
        {
            description: `${car.model} ${car.chassisNumber}`,
        },
        {
            title: t('confirm_remove_this_car_forever'),
            fullScreen: false,
        })
        .then(() => Api.cars.remove(car.id))
        .finally(hideModal);

    const openFilterModal = (filterInputs: ReactNode) => showModal(FilterDialog,
        {
            filterInputs
        },
        {
            title: t('filter'),
        })
        .then(data => {
            hideModal();
            return data;
        });

    const openActivity = (id: number) => Api.util.activityShow('car', id)
        .then(data => showModal(ActivityDialog,
            {activity: data.data},
            {
                title: t('activity'),
                maxWidth: 'lg',
                fullWidth: true,
            }));

    const openYardActivity = (activity: CarYard[]) => showModal(YardActivityDialog,
            {activity},
            {
                title: t('activity'),
                maxWidth: 'md',
                fullWidth: true,
            });

    const confirmAnshin = () => showModal(ConfirmDialog,
        {
            description: t('confirm_anshin_description'),
        },
        {
            title: t('confirm_anshin'),
            fullScreen: false,
        })
        .then(Api.util.forceAnshin)
        .finally(hideModal);

    const confirmCancelTransportation = (tr: TransportationModel, car: CarModel) => showModal(PromptDialog,
        {
            description: `${car.make} ${car.model} ${car.chassisNumber}`,
            required: true
        },
        {
            title: t('confirm_cancel_transportation'),
            fullScreen: false,
        })
        .then(reason => Api.transportation.cancel(tr.id, reason))
        .finally(hideModal);

    return {
        openAddCarModal,
        openEditCarModal,
        openViewPhotosModal,
        openAddPhotosModal,
        openYardModal,
        openVoyageModal,
        confirmCancelShipping,
        confirmCancelBooking,
        openBatchCarCreateModal,
        openBatchInYardModal,
        openBatchUploadECModal,
        openBatchUploadECMultiPageModal,
        openBatchUploadPhotosModal,
        openYardInModal,
        openInnerCargoModal,
        openChangeChassisModal,
        openYardInSingleModal,
        openChangeShipperModal,
        openShipDateLimitModal,
        openEditNotesModal,
        confirmRestoreCar,
        confirmRemoveCar,
        openFilterModal,
        openActivity,
        openYardActivity,
        confirmAnshin,
        confirmCancelTransportation,
    };
};

export default useCarModals;
